import { Checkbox } from "@/components/ui/checkbox";
import type { CatalogListItem } from "@/lib/catalog-item";
import type { CatalogSelection } from "@/lib/catalog-selection";

// The selection itself is `@/lib/catalog-selection` — the tick draws it, it does
// not own it. Re-exported so the views keep one import for the whole contract.
export type { CatalogSelection };

// The batch-mode tick in an item's top-right corner.
//
// The donor puts a bare `<input type='checkbox'>` at `top: 8px; right: 8px;
// zIndex: 3` on both the card and the list row, and — the part that matters —
// **in place of the 3-dot actions button**, not beside it (`{selectable && …}`
// / `{!selectable && showItemActions && …}`). One corner, one control: in batch
// mode the corner selects, and out of it the corner acts on the single item.
// The Atlas `Checkbox` primitive draws it, so batch mode looks like the rest of
// the app rather than like the browser's default control.
//
// It names the item it selects. "Select" on twenty adjacent checkboxes is twenty
// controls a screen reader cannot tell apart.

/**
 * What a view needs to draw ticks — **present or absent as one thing**, never
 * three independently optional fields.
 *
 * A view is either in batch mode, in which case it has all of it, or it is not,
 * in which case it has none: the item detail page and the card's own field tests
 * render without any. Modelling that as three optionals made every call site
 * write `selectable && selected && onToggleSelect &&` to convince the compiler
 * of something the caller already knew, and let a caller pass one without the
 * others.
 */
export interface ItemSelectionProps {
  selection?: ItemSelection;
}

/** Batch mode's three, which only ever travel together. */
export interface ItemSelection {
  selected: CatalogSelection;
  onToggleSelect: (item: CatalogListItem) => void;
}

type ItemSelectCheckboxProps = ItemSelection & { item: CatalogListItem };

export function ItemSelectCheckbox({ item, selected, onToggleSelect }: ItemSelectCheckboxProps) {
  return (
    <Checkbox
      // `z-3` is the donor's own `zIndex: 3`, kept so the tick sits above the
      // card's image and its EI-tier logo (`z-[2]`). It is not what separates it
      // from the status overlay — that one is `top-0 left-0 z-10`, the opposite
      // corner and higher; the two never meet.
      className="absolute top-2 right-2 z-3 bg-white"
      checked={selected.has(item.id)}
      onCheckedChange={() => onToggleSelect(item)}
      aria-label={`Select ${item.name}`}
    />
  );
}
